import { useContext } from 'react';
import { Grid, Box, Stack } from '@mui/material';
import {
    DataGrid,
    GridColDef,
    GridToolbar,
    GridToolbarContainer,
    GridRenderCellParams,
    GridValueGetterParams,
} from '@mui/x-data-grid';
import CustomPagination from '../../GlobalComponent/CustomPagination/CustomPagination';
import pendingRequestsStyle from '../../Styles/ComponentStyles/PendingRequests/PendingRequests.module.scss';
import adminDashboardStyles from '../../Styles/ComponentStyles/AdminDashboard/AdminDashboard.module.scss';
import { useSnackbar } from '../../GlobalComponent/GlobalSnackbar/GlobalSnackbar';
import { UserContext, UserContextPayload } from '../../Context/UserDetailContext';
import agent from '../../service/api';
import { LicensesHoldCell } from './LicensesHoldCell';
import { renderOverflowTextCell } from './OverflowTooltipCell';
import { RejectedReasonCell } from './RejectedReasonCell';

export interface RejectedAgentRow {
    user_id: number;
    name: string;
    email: string;
    requestedDate: string;
    rejectedDate: string;
    rejectedBy: string;
    rejectedReason: string;
    licensesHold: string[];
    [key: string]: unknown;
}

interface RejectedRequestsListProps {
    rows: RejectedAgentRow[];
    onRefresh: () => void;
}

const adminDashboardDataGridSx = {
    borderRadius: '5px',
    overflow: 'hidden',
    "& .MuiDataGrid-columnHeaders": {
        backgroundColor: "#F2F6F9",
        color: '#45556C',
        fontSize: '14px',
        fontWeight: 600,
        textTransform: 'uppercase',
        border: 'none',
        minHeight: "54px !important"
    },
    "& .MuiDataGrid-columnHeaderTitleContainer": {
        justifyContent: "start !important"
    },
    "& .MuiDataGrid-columnHeader": {
        padding: '0 16px',
        outline: 'none !important',
        justifyContent: 'flex-start',
    },
    "& .MuiDataGrid-columnHeaderTitle": {
        fontWeight: 600,
        textAlign: 'left',
    },
    "& .MuiDataGrid-main": {
        backgroundColor: "#ffffff",
        borderRadius: "5px 5px 0px 0px",
        borderTop: "1px solid #E2E8F0",
        borderLeft: "1px solid #E2E8F0",
        borderRight: "1px solid #E2E8F0",
        borderBottom: "none",
    },
    "& .MuiDataGrid-row": {
        backgroundColor: "#ffffff",
        color: "#0F172B",
        borderBottom: '1px solid #E2E8F0 !important',
        '&:hover': {
            backgroundColor: '#f3f4f6 !important',
        },
    },
    "& .MuiDataGrid-cell": {
        borderBottom: 'none',
        padding: '0 16px',
        fontSize: '14px !important',
        outline: 'none !important',
    },
    "& .MuiDataGrid-cell > div": {
        width: '100%',
        minWidth: 0,
        overflow: 'hidden',
    },
    "& .MuiDataGrid-cell:focus": {
        outline: 'none !important',
    },
    "& .MuiDataGrid-cell:focus-within": {
        outline: 'none !important',
    },
    "& .MuiDataGrid-cell:hover": {
        cursor: "pointer",
    },
    "& .MuiDataGrid-columnSeparator": {
        display: 'none',
    },
    "& .MuiDataGrid-footerContainer": {
        borderTop: "none",
        borderLeft: "1px solid #E2E8F0",
        borderRight: "1px solid #E2E8F0",
        borderBottom: "1px solid #E2E8F0",
        backgroundColor: '#F2F6F9',
        minHeight: '52px',
        padding: '0 16px',
        borderRadius: "0px 0px 5px 5px"
    },
    "& .MuiTablePagination-root": {
        color: '#6b7280',
    },
    "& .MuiTablePagination-actions": {
        color: '#6b7280',
    },
    "& .MuiTablePagination-actions .MuiIconButton-root": {
        color: "#6b7280",
    },
    "& .MuiTablePagination-actions .MuiIconButton-root.Mui-disabled": {
        color: "#d1d5db",
    },
    "& .MuiTablePagination-displayedRows": {
        color: '#6b7280',
        fontSize: '14px',
    },
    "& .MuiDataGrid-toolbarContainer .MuiButtonBase-root": {
        color: "#525252 !important"
    },
    "& .MuiDataGrid-toolbarContainer": {
        margin: "5px !important"
    },
    "& .MuiDataGrid-overlay": {
        backgroundColor: "#ffffff",
    },
    "& .MuiDataGrid-columnHeader--moving": {
        backgroundColor: "transparent",
    },
    "& .MuiDataGrid-virtualScrollerContent": {
        marginTop: "18px"
    }
} as const;

const CustomToolbar = () => (
    <GridToolbarContainer
        sx={{
            margin: '5px 0px 25px',
            float: 'right',
            '& .MuiButtonBase-root': { color: '#ffffff !important' },
        }}
    >
        <GridToolbar />
    </GridToolbarContainer>
);

function NoRowsOverlay() {
    return (
        <Stack sx={{ justifyContent: 'center', height: '100%', alignItems: 'center', color: '#0F172B' }}>
            No Data Found
        </Stack>
    );
}

const RejectedRequestsList = ({ rows, onRefresh }: RejectedRequestsListProps) => {

    const { showSnackbar } = useSnackbar();
    const { setFlagLoader } = useContext(UserContext) as UserContextPayload;

    const handleApprove = async (row: RejectedAgentRow) => {
        if (!row.user_id || Number.isNaN(row.user_id)) {
            showSnackbar('Invalid user.', 'error');
            return;
        }
        try {
            setFlagLoader(true);
            const response = await agent.approveAgentAPI.post({ user_id: row.user_id });
            if (response?.isAxiosError) {
                showSnackbar('Failed to approve agent.', 'error');
                return;
            }
            if (response?.status) {
                showSnackbar(
                    response?.data?.message || 'User successfully authorized.',
                    'success'
                );
                onRefresh();
            } else {
                showSnackbar(
                    response?.data?.error?.message ||
                    response?.error?.message ||
                    'Failed to approve agent.',
                    'error'
                );
            }
        } catch {
            showSnackbar('Failed to approve agent.', 'error');
        } finally {
            setFlagLoader(false);
        }
    };

    const columns: GridColDef[] = [
        {
            field: '__index',
            headerName: '#',
            width: 50,
            sortable: false,
            disableColumnMenu: true,
            valueGetter: (params: GridValueGetterParams) => {
                const ids = params.api.getAllRowIds();
                const i = ids.indexOf(params.id);
                return i === -1 ? '' : i + 1;
            },
        },
        { field: 'name', headerName: 'NAME', flex: 1, minWidth: 130, width: 130, renderCell: renderOverflowTextCell },
        { field: 'email', headerName: 'EMAIL', flex: 1.3, minWidth: 200, width: 200, renderCell: renderOverflowTextCell },
        {
            field: 'licensesHold',
            headerName: 'LICENSES HELD',
            flex: 1.2,
            minWidth: 180,
            sortable: false,
            renderCell: (params: GridRenderCellParams<string[], RejectedAgentRow>) => (
                <LicensesHoldCell licenses={params.row.licensesHold ?? []} />
            ),
        },
        { field: 'requestedDate', headerName: 'REQUESTED DATE', flex: 1.2, minWidth: 180, renderCell: renderOverflowTextCell },
        { field: 'rejectedDate', headerName: 'REJECTED DATE', flex: 1.2, minWidth: 180, renderCell: renderOverflowTextCell },
        { field: 'rejectedBy', headerName: 'REJECTED BY', flex: 0.9, minWidth: 120, renderCell: renderOverflowTextCell },
        {
            field: 'rejectedReason',
            headerName: 'REJECTED REASON',
            flex: 1.5,
            minWidth: 220,
            sortable: false,
            renderCell: (params: GridRenderCellParams<string, RejectedAgentRow>) => (
                <RejectedReasonCell reason={params.value ?? ''} />
            ),
        },
        {
            field: 'action',
            headerName: 'ACTION',
            flex: 0.9,
            minWidth: 140,
            sortable: false,
            disableColumnMenu: true,
            renderCell: (params: GridRenderCellParams<unknown, RejectedAgentRow>) => (
                <Box className={pendingRequestsStyle.actionCell}>
                    <button type="button" className={pendingRequestsStyle.approveBtn} onClick={() => handleApprove(params.row)}>
                        Approve
                    </button>
                </Box>
            ),
        },
    ];

    return (
        <Grid container direction="column" className={pendingRequestsStyle.listWrapper}>
            <Grid item xs={12} className={pendingRequestsStyle.tableWrapper}>
                <DataGrid
                    className={adminDashboardStyles.adminDashboardTable}
                    rows={rows}
                    columns={columns}
                    getRowId={(r: RejectedAgentRow) => r.user_id}
                    density="compact"
                    hideFooterSelectedRowCount
                    rowHeight={60}
                    pageSize={10}
                    rowsPerPageOptions={[5]}
                    disableSelectionOnClick
                    autoHeight
                    components={{ Pagination: CustomPagination, Toolbar: CustomToolbar, NoRowsOverlay }}
                    sx={adminDashboardDataGridSx}
                />
            </Grid>
        </Grid>
    );
};

export default RejectedRequestsList;